// The stored shape of the browser wallet's key (plan 2026-10-06-snappy-key-vault.md §2): the localStorage value of
// `snappy.browserWallet.key.v1` is a key vault envelope (`v: 2`, the key encrypted under a PIN or a passkey) or, from
// before the vault, a legacy clear record (`v: 1`, migrated on sight). Parsing only: no crypto and no dependencies, so
// the engine-free peek (`../keyRecordPeek.ts`, in the V1 entry) and the vendored keys shim share one strict parser.
//
// Security: a legacy record holds the master phrase in clear. Never log, echo or copy what this module parses.

export type KeyVaultMethod = 'pin' | 'passkey';

/** scrypt parameters of a PIN envelope. */
export interface PinKdf {
    N: number;
    r: number;
    p: number;
}

/** The passkey a passkey envelope opens with (WebAuthn PRF, §2.2). */
export interface KeyVaultPasskey {
    /** base64url */
    credentialId: string;
    /** 32 bytes hex: the PRF eval input. */
    prfSalt: string;
    transports?: string[];
}

export interface KeyVaultEnvelope {
    v: 2;
    /** Checksummed, in clear: the UI shows which wallet is sealed here. */
    address: `0x${string}`;
    userId: string | null;
    method: KeyVaultMethod;
    /** scrypt params, PIN only. */
    kdf: PinKdf | null;
    /** 16 bytes hex: the scrypt salt (PIN) or the HKDF salt (passkey). */
    salt: string;
    /** 12 bytes hex, AES-GCM. */
    nonce: string;
    /** hex, AES-256-GCM ciphertext + 16-byte tag. */
    ct: string;
    passkey: KeyVaultPasskey | null;
    createdAt: number;
}

/** The legacy clear record (`v: 1`). */
export interface LegacyKeyRecord {
    mnemonic: string;
    address: `0x${string}`;
    userId: string | null;
}

export type StoredKey =
    | { kind: 'envelope'; envelope: KeyVaultEnvelope }
    | { kind: 'legacy'; record: LegacyKeyRecord };

/** PIN work factor bounds (§2.1): N a power of two in [2^14, 2^17], r = 8, p in [1, 8]. */
export const PIN_KDF_BOUNDS = {
    minLogN: 14,
    maxLogN: 17,
    r: 8,
    minP: 1,
    maxP: 8,
} as const;

export const VAULT_SALT_BYTES = 16;
export const VAULT_NONCE_BYTES = 12;
export const VAULT_TAG_BYTES = 16;
export const PRF_SALT_BYTES = 32;
/** Far above a 24-word phrase's JSON plaintext (under 300 bytes): a longer one is not a vault. */
export const VAULT_MAX_CT_BYTES = 2048;

const ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/;
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]{1,1400}$/;
const MAX_TRANSPORTS = 8;
const MAX_TRANSPORT_LENGTH = 32;

function isHex(value: unknown, bytes: number): value is string {
    return (
        typeof value === 'string' &&
        value.length === bytes * 2 &&
        /^[0-9a-fA-F]*$/.test(value)
    );
}

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The PIN kdf when `value` is one within bounds, else null. */
export function parsePinKdf(value: unknown): PinKdf | null {
    if (!isObject(value)) return null;
    const { N, r, p } = value;
    if (
        typeof N !== 'number' ||
        typeof r !== 'number' ||
        typeof p !== 'number' ||
        !Number.isInteger(N) ||
        !Number.isInteger(p) ||
        N < 2 ** PIN_KDF_BOUNDS.minLogN ||
        N > 2 ** PIN_KDF_BOUNDS.maxLogN ||
        (N & (N - 1)) !== 0 ||
        r !== PIN_KDF_BOUNDS.r ||
        p < PIN_KDF_BOUNDS.minP ||
        p > PIN_KDF_BOUNDS.maxP
    ) {
        return null;
    }
    return { N, r, p };
}

function parsePasskey(value: unknown): KeyVaultPasskey | null {
    if (!isObject(value)) return null;
    const { credentialId, prfSalt, transports } = value;
    if (
        typeof credentialId !== 'string' ||
        !BASE64URL_PATTERN.test(credentialId) ||
        !isHex(prfSalt, PRF_SALT_BYTES)
    ) {
        return null;
    }
    if (transports === undefined) return { credentialId, prfSalt };
    if (
        !Array.isArray(transports) ||
        transports.length > MAX_TRANSPORTS ||
        !transports.every(
            (transport) =>
                typeof transport === 'string' &&
                transport.length > 0 &&
                transport.length <= MAX_TRANSPORT_LENGTH,
        )
    ) {
        return null;
    }
    return { credentialId, prfSalt, transports: [...(transports as string[])] };
}

function isCiphertext(value: unknown): value is string {
    return (
        typeof value === 'string' &&
        value.length % 2 === 0 &&
        value.length >= (VAULT_TAG_BYTES + 1) * 2 &&
        value.length <= VAULT_MAX_CT_BYTES * 2 &&
        /^[0-9a-fA-F]*$/.test(value)
    );
}

/**
 * A strict parse of a key vault envelope (an already parsed JSON value): the shape, the hex lengths, the PIN kdf
 * bounds, and method / kdf / passkey consistency. Null on anything else. Returns a clean copy (known fields only).
 */
export function parseEnvelope(value: unknown): KeyVaultEnvelope | null {
    if (!isObject(value) || value.v !== 2) return null;
    const { address, userId, method, salt, nonce, ct, createdAt } = value;
    if (
        typeof address !== 'string' ||
        !ADDRESS_PATTERN.test(address) ||
        (userId !== null && typeof userId !== 'string') ||
        (method !== 'pin' && method !== 'passkey') ||
        !isHex(salt, VAULT_SALT_BYTES) ||
        !isHex(nonce, VAULT_NONCE_BYTES) ||
        !isCiphertext(ct) ||
        typeof createdAt !== 'number' ||
        !Number.isInteger(createdAt) ||
        createdAt < 0
    ) {
        return null;
    }
    let kdf: PinKdf | null = null;
    let passkey: KeyVaultPasskey | null = null;
    if (method === 'pin') {
        kdf = parsePinKdf(value.kdf);
        if (kdf === null || value.passkey !== null) return null;
    } else {
        passkey = parsePasskey(value.passkey);
        if (passkey === null || value.kdf !== null) return null;
    }
    return {
        v: 2,
        address: address as `0x${string}`,
        userId,
        method,
        kdf,
        salt,
        nonce,
        ct,
        passkey,
        createdAt,
    };
}

function parseLegacy(value: Record<string, unknown>): LegacyKeyRecord | null {
    const { mnemonic, address, userId } = value;
    if (
        typeof mnemonic !== 'string' ||
        mnemonic.length === 0 ||
        typeof address !== 'string' ||
        !ADDRESS_PATTERN.test(address) ||
        (userId !== undefined && userId !== null && typeof userId !== 'string')
    ) {
        return null;
    }
    return {
        mnemonic,
        address: address as `0x${string}`,
        userId: userId ?? null,
    };
}

// one warn per run of unreadable values (a bad value is read on every refresh): the tag only, never the value
let warnedUnreadable = false;

/**
 * The stored key in `raw` (the localStorage value): an envelope, a legacy clear record, or null for nothing or
 * anything unreadable (which reads as "no wallet held", with a console warn tag, never a crash).
 */
export function parseStoredKey(
    raw: string | null | undefined,
): StoredKey | null {
    if (raw === null || raw === undefined || raw === '') return null;
    let parsed: StoredKey | null = null;
    try {
        const value: unknown = JSON.parse(raw);
        if (isObject(value)) {
            if (value.v === 2) {
                const envelope = parseEnvelope(value);
                if (envelope) parsed = { kind: 'envelope', envelope };
            } else if (value.v === 1) {
                const record = parseLegacy(value);
                if (record) parsed = { kind: 'legacy', record };
            }
        }
    } catch {
        parsed = null;
    }
    if (parsed === null) {
        if (!warnedUnreadable) {
            warnedUnreadable = true;
            console.warn('[keyVault] the stored key is unreadable');
        }
        return null;
    }
    warnedUnreadable = false;
    return parsed;
}

/** The address a stored key holds (in clear in both shapes). */
export function storedKeyAddress(stored: StoredKey): `0x${string}` {
    return stored.kind === 'envelope'
        ? stored.envelope.address
        : stored.record.address;
}
